import React, { useCallback, useEffect, useState } from 'react';
import { AgentDef, agentsAPI, ProviderSetting, providerSettingsAPI } from '../../api/client';
import { ModelSelect } from './ModelSelect';
import { useViewport } from '../../hooks/useViewport';
import { useFeature } from '../../hooks/useFeature';
import { AGENT_PROVIDERS, FeatureKey } from '../../generated/contract';

// Used until the provider settings load (or if they fail to) — the server
// returns the same list, in the same order, with each provider's models:
// providers.KnownProviders, read from the generated contract (refactor plan
// X4b).
const FALLBACK_PROVIDERS: string[] = [...AGENT_PROVIDERS];

// The antigravity-cli provider ships behind its release feature (REQ-137):
// a stable-channel workspace is offered it once its stable release carries
// the feature. An agent already on it keeps it in the picker, so editing that
// agent never silently moves it to another provider.
export const ANTIGRAVITY_FEATURE: FeatureKey = 'antigravity-cli';

interface AgentEditorProps {
  agent: AgentDef | null; // null = creating a new agent
  onSaved: () => void;
  onCancel: () => void;
}

// Reasoning effort tiers (Claude Code passes them through; Codex maps
// xhigh/max down to high; Gemini has no headless effort control yet).
const EFFORT_LEVELS = ['low', 'medium', 'high', 'xhigh', 'max'];

// The API's own wording for a definition with no tool allowlist (it answers
// 400 with it). Shown here before the request goes out, so the reason arrives
// at the same moment as the refusal.
export const ALLOWED_TOOLS_REQUIRED =
  'agent definition requires allowed_tools: every agent must name the tools its vendor CLI may use (e.g. mcp__openv__*), because a CLI started with no allowlist runs with all of them';

interface FormState {
  slug: string;
  name: string;
  description: string;
  provider: string;
  model: string;
  effort: string;
  write_mode: 'proposal' | 'direct';
  repo_access: boolean;
  locked: boolean;
  max_turns: number;
  timeout_seconds: number;
  allowed_tools: string;
  system_prompt: string;
}

const toForm = (agent: AgentDef | null): FormState => ({
  slug: agent?.slug || '',
  name: agent?.name || '',
  description: agent?.description || '',
  provider: agent?.provider || 'claude-code',
  model: agent?.model || '',
  effort: agent?.effort || '',
  write_mode: agent?.write_mode || 'proposal',
  repo_access: agent?.repo_access || false,
  locked: agent?.locked || false,
  max_turns: agent?.max_turns ?? 30,
  timeout_seconds: agent?.timeout_seconds ?? 600,
  allowed_tools: (agent?.allowed_tools || []).join(', '),
  system_prompt: agent?.system_prompt || '',
});

export const AgentEditor: React.FC<AgentEditorProps> = ({ agent, onSaved, onCancel }) => {
  const { isPhone } = useViewport();
  const isNew = !agent;
  const [mode, setMode] = useState<'form' | 'raw'>('form');
  const [form, setForm] = useState<FormState>(toForm(agent));
  const [raw, setRaw] = useState('');
  const [rawLoaded, setRawLoaded] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [providerSettings, setProviderSettings] = useState<ProviderSetting[]>([]);
  const antigravityOn = useFeature(ANTIGRAVITY_FEATURE);

  // Provider settings carry each provider's available models; a failure here
  // is not worth an error banner — the form falls back to free-text entry.
  useEffect(() => {
    providerSettingsAPI
      .list()
      .then((res) => setProviderSettings(res.data || []))
      .catch(() => setProviderSettings([]));
  }, []);

  useEffect(() => {
    setForm(toForm(agent));
    setMode('form');
    setRaw('');
    setRawLoaded(false);
    setError('');
  }, [agent]);

  const loadRaw = useCallback(() => {
    if (!agent) return;
    agentsAPI
      .raw(agent.slug)
      .then((res) => {
        setRaw(res.data.content);
        setRawLoaded(true);
      })
      .catch((err: any) =>
        setError(err.response?.data?.error || err.message || 'Failed to load raw markdown')
      );
  }, [agent]);

  useEffect(() => {
    if (mode === 'raw' && !rawLoaded) loadRaw();
  }, [mode, rawLoaded, loadRaw]);

  const set = (update: Partial<FormState>) => setForm((f) => ({ ...f, ...update }));

  // Every agent must name the tools its CLI may use: an empty allowlist is not
  // "no tools", it is every tool the vendor CLI has. The API refuses it with a
  // 400; the form refuses it here so nobody has to find that out by saving.
  const allowedTools = form.allowed_tools
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const saveForm = async () => {
    if (allowedTools.length === 0) {
      setError(ALLOWED_TOOLS_REQUIRED);
      return;
    }
    setSaving(true);
    setError('');
    const payload: Partial<AgentDef> = {
      slug: form.slug.trim(),
      name: form.name.trim(),
      description: form.description,
      provider: form.provider,
      model: form.model,
      effort: form.effort,
      write_mode: form.write_mode,
      repo_access: form.repo_access,
      locked: form.locked,
      max_turns: Number(form.max_turns) || 0,
      timeout_seconds: Number(form.timeout_seconds) || 0,
      allowed_tools: allowedTools,
      system_prompt: form.system_prompt,
    };
    try {
      if (isNew) {
        await agentsAPI.create(payload);
      } else {
        await agentsAPI.update(agent!.slug, payload);
      }
      onSaved();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save agent');
    } finally {
      setSaving(false);
    }
  };

  const saveRawContent = async () => {
    if (!agent) return;
    setSaving(true);
    setError('');
    try {
      await agentsAPI.saveRaw(agent.slug, raw);
      onSaved();
    } catch (err: any) {
      // Show API validation errors verbatim.
      const data = err.response?.data;
      setError(
        typeof data === 'string' ? data : data?.error || JSON.stringify(data) || err.message
      );
    } finally {
      setSaving(false);
    }
  };

  const activeProvider = providerSettings.find((p) => p.provider === form.provider);
  const providerNames = (providerSettings.length ? providerSettings.map((p) => p.provider) : FALLBACK_PROVIDERS).filter(
    (p) => p !== 'antigravity-cli' || antigravityOn || agent?.provider === p
  );

  return (
    <div className="card measure" style={{ marginBottom: 0 }} data-help-pane="agents.editor" data-help-key="agents.editor">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, flex: '1 1 160px', minWidth: 0, overflowWrap: 'anywhere' }}>{isNew ? 'New agent' : `Edit: ${agent?.name}`}</h3>
        {!isNew && (
          <div style={{ display: 'inline-flex', flexShrink: 0, border: '1px solid var(--border)', borderRadius: 4, overflow: 'hidden' }}>
            {(['form', 'raw'] as const).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                style={{
                  padding: '5px 12px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 12,
                  background: mode === m ? 'var(--accent)' : 'var(--surface)',
                  color: mode === m ? 'var(--accent-fg)' : 'var(--text)',
                }}
              >
                {m === 'form' ? 'Form' : 'Raw markdown'}
              </button>
            ))}
          </div>
        )}
        <button
          onClick={onCancel}
          aria-label="Close editor"
          style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text-muted)', width: 40, minHeight: 40, padding: 0, flexShrink: 0 }}
          title="Close editor"
        >
          ×
        </button>
      </div>

      {error && (
        <pre
          style={{
            background: 'var(--tint-red)',
            border: '1px solid var(--danger)',
            color: 'var(--danger-strong)',
            borderRadius: 4,
            padding: '8px 12px',
            marginBottom: 12,
            fontSize: 12,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {error}
        </pre>
      )}

      {mode === 'form' ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: isPhone ? '1fr' : '1fr 1fr', gap: '0 14px' }}>
            <div className="form-group">
              <label>Slug</label>
              <input
                value={form.slug}
                disabled={!isNew}
                onChange={(e) => set({ slug: e.target.value })}
                placeholder="my-agent"
                style={{ fontSize: 13, background: isNew ? undefined : 'var(--surface-alt)' }}
              />
            </div>
            <div className="form-group">
              <label>Name</label>
              <input
                value={form.name}
                onChange={(e) => set({ name: e.target.value })}
                style={{ fontSize: 13 }}
              />
            </div>
            <div className="form-group" style={{ gridColumn: isPhone ? undefined : '1 / span 2' }}>
              <label>Description</label>
              <input
                value={form.description}
                onChange={(e) => set({ description: e.target.value })}
                style={{ fontSize: 13 }}
              />
            </div>
            <div className="form-group" data-help-key="agents.model">
              <label>Provider</label>
              <select
                value={form.provider}
                onChange={(e) => set({ provider: e.target.value })}
                style={{ fontSize: 13 }}
              >
                {providerNames.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group" data-help-key="agents.model">
              <label>Model</label>
              <ModelSelect
                value={form.model}
                models={activeProvider?.available_models || []}
                onChange={(model) => set({ model })}
                emptyLabel={
                  activeProvider?.default_model
                    ? `provider default (${activeProvider.default_model})`
                    : 'provider default'
                }
                style={{ fontSize: 13 }}
              />
            </div>
            <div className="form-group" data-help-key="agents.model">
              <label>Effort</label>
              <select
                value={form.effort}
                onChange={(e) => set({ effort: e.target.value })}
                title="Reasoning effort per run. Applies to claude-code (low…max) and codex-cli (capped at high); gemini-cli ignores it."
                style={{ fontSize: 13 }}
              >
                <option value="">provider default</option>
                {EFFORT_LEVELS.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group" data-help-key="agents.write-mode">
              <label>Write mode</label>
              <select
                value={form.write_mode}
                onChange={(e) => set({ write_mode: e.target.value as 'proposal' | 'direct' })}
                style={{ fontSize: 13 }}
              >
                <option value="proposal">proposal (changes need approval)</option>
                <option value="direct">direct (writes immediately)</option>
              </select>
            </div>
            <div
              className="form-group"
              data-help-key="agents.repos"
              style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 24 }}
            >
              <input
                id="repo-access"
                type="checkbox"
                checked={form.repo_access}
                onChange={(e) => set({ repo_access: e.target.checked })}
                style={{ width: 'auto' }}
              />
              <label htmlFor="repo-access" style={{ margin: 0, fontWeight: 400 }}>
                Repository access
              </label>
            </div>
            <div className="form-group">
              <label>Max turns</label>
              <input
                type="number"
                value={form.max_turns}
                onChange={(e) => set({ max_turns: Number(e.target.value) })}
                style={{ fontSize: 13 }}
              />
            </div>
            <div className="form-group">
              <label>Timeout (seconds)</label>
              <input
                type="number"
                value={form.timeout_seconds}
                onChange={(e) => set({ timeout_seconds: Number(e.target.value) })}
                style={{ fontSize: 13 }}
              />
            </div>
            <div
              className="form-group"
              data-help-key="agents.allowlist"
              style={{ gridColumn: isPhone ? undefined : '1 / span 2' }}
            >
              <label>Allowed tools (comma-separated, required)</label>
              <input
                value={form.allowed_tools}
                onChange={(e) => set({ allowed_tools: e.target.value })}
                placeholder="e.g. mcp__openv__*, Read, WebSearch"
                aria-invalid={allowedTools.length === 0}
                style={{
                  fontSize: 13,
                  borderColor: allowedTools.length === 0 ? 'var(--danger)' : undefined,
                }}
              />
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>
                {allowedTools.length === 0
                  ? 'Required: an empty list means the vendor CLI runs with every tool it has, so the agent cannot be saved without one.'
                  : 'mcp__openv__* grants the OpenV tools. Add vendor tools (Read, Edit, WebSearch…) only where this agent needs them.'}
              </div>
            </div>
            <div className="form-group" style={{ gridColumn: isPhone ? undefined : '1 / span 2' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <input
                  id="agent-locked"
                  type="checkbox"
                  checked={form.locked}
                  onChange={(e) => set({ locked: e.target.checked })}
                  style={{ width: 'auto', marginTop: 3 }}
                />
                <div>
                  <label htmlFor="agent-locked" style={{ margin: 0, fontWeight: 400 }}>
                    Lock this agent against automatic updates
                  </label>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, maxWidth: 'var(--measure-field)' }}>
                    Platform releases bring a standard agent's prompt and tools up to date
                    where you have not changed them. Lock it and they never will — this agent
                    then changes only when someone here changes it. You can unlock it again at
                    any time.
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="form-group">
            <label>System prompt</label>
            <textarea
              value={form.system_prompt}
              onChange={(e) => set({ system_prompt: e.target.value })}
              style={{ minHeight: 220, fontFamily: 'monospace', fontSize: 12.5 }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="button"
              onClick={saveForm}
              disabled={saving || !form.slug.trim() || !form.name.trim() || allowedTools.length === 0}
            >
              {saving ? 'Saving…' : isNew ? 'Create agent' : 'Save changes'}
            </button>
            <button className="button-secondary" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          <textarea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            spellCheck={false}
            style={{
              minHeight: 420,
              fontFamily: 'monospace',
              fontSize: 12.5,
              width: '100%',
              marginBottom: 12,
            }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="button" onClick={saveRawContent} disabled={saving || !rawLoaded}>
              {saving ? 'Saving…' : 'Save raw file'}
            </button>
            <button className="button-secondary" onClick={loadRaw}>
              Reload from server
            </button>
          </div>
        </>
      )}
    </div>
  );
};
